import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  FolderKanban, CheckCircle, AlertTriangle, Zap, Clock, TrendingUp,
  AlertCircle, Camera, Package, Users, ArrowUpRight, ArrowDownRight,
  ArrowRight, BarChart3, FileText, Download, Activity, Calendar
} from 'lucide-react';

const COLORS = {
  amber: '#F59E0B', blue: '#3B82F6', green: '#10B981', red: '#EF4444',
  purple: '#8B5CF6', cyan: '#06B6D4', pink: '#EC4899', gray: '#64748B',
};
const PIE_COLORS = [COLORS.green, COLORS.blue, COLORS.amber, COLORS.red, COLORS.gray, COLORS.purple];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1E293B', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.4)', fontSize: 12
    }}>
      <p style={{ color: '#94A3B8', marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('es-ES') : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [photoActivity, setPhotoActivity] = useState([]);
  const [taskDist, setTaskDist] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [sumRes, projRes, kpiRes, photoRes, taskRes] = await Promise.all([
        api.get('/dashboard/summary'),
        api.get('/projects', { params: { limit: 6, status: 'in_progress' } }),
        api.get('/analytics/kpis').catch(() => ({ data: null })),
        api.get('/analytics/photo-activity', { params: { days: 14 } }).catch(() => ({ data: [] })),
        api.get('/analytics/task-distribution').catch(() => ({ data: null })),
      ]);
      setData(sumRes.data);
      setProjects(projRes.data.data || projRes.data || []);
      setKpis(kpiRes.data);
      setPhotoActivity(photoRes.data || []);
      setTaskDist(taskRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const p = data?.projects || {};
  const m = data?.milestones || {};
  const t = data?.tasks || {};
  const todayKpi = kpis?.today || {};
  const trends = kpis?.trends || {};

  // Prepare chart data
  const taskStatusData = (taskDist?.byStatus || []).map(s => ({
    name: statusLabelShort(s.status), value: parseInt(s.count), status: s.status
  }));

  const photoChartData = (photoActivity || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    total: parseInt(d.total),
    instalación: parseInt(d.installation || 0),
    avance: parseInt(d.progress || 0),
    defecto: parseInt(d.defect || 0),
  }));

  const categoryData = (taskDist?.byCategory || []).map(c => ({
    name: c.code || c.category || '?',
    total: parseInt(c.total),
    completed: parseInt(c.completed),
    progress: parseFloat(c.avg_progress) || 0,
  }));

  return (
    <div className="page-dashboard">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>¡Hola, {user?.firstName}!</h1>
          <p className="subtitle">Panel de control — {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/analytics')}>
            <BarChart3 size={16} /> Analytics
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/reports')}>
            <FileText size={16} /> Reportes
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/projects')}>
            <FolderKanban size={16} /> Proyectos
          </button>
        </div>
      </div>

      {/* Overdue alert */}
      {parseInt(m.overdue || 0) > 0 && (
        <div className="alert alert-warning">
          <AlertCircle size={18} />
          <strong>{m.overdue} hito(s) contractual(es) vencido(s)</strong> — Requieren atención inmediata.
        </div>
      )}

      {/* KPI Cards Row 1: Main metrics */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <KpiCard icon={<FolderKanban size={22} />} color="blue"
          value={p.total || 0} label="Proyectos" badge={`${p.active || 0} activos`} badgeColor="blue"
          onClick={() => navigate('/projects')} />
        <KpiCard icon={<Zap size={22} />} color="green"
          value={`${Number(p.total_kwp || 0).toLocaleString()}`} label="kWp totales" />
        <KpiCard icon={<CheckCircle size={22} />} color="emerald"
          value={`${t.completed || 0}/${t.total || 0}`} label="Tareas completadas"
          badge={`${t.total > 0 ? Math.round((t.completed / t.total) * 100) : 0}%`} badgeColor="emerald" />
        <KpiCard icon={<AlertTriangle size={22} />} color="amber"
          value={m.overdue || 0} label="Hitos vencidos"
          badge={`${m.completed || 0}/${m.total || 0}`} badgeColor="amber" />
      </div>

      {/* KPI Cards Row 2: Today's activity */}
      {kpis && (
        <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginTop: -8 }}>
          <KpiCard icon={<Camera size={20} />} color="purple" small
            value={todayKpi.photos_today || 0} label="Fotos hoy"
            trend={trends.photos} />
          <KpiCard icon={<CheckCircle size={20} />} color="green" small
            value={todayKpi.tasks_completed_today || 0} label="Tareas hoy"
            trend={trends.tasks} />
          <KpiCard icon={<Package size={20} />} color="blue" small
            value={todayKpi.equipment_today || 0} label="Equipos hoy" />
          <KpiCard icon={<Users size={20} />} color="amber" small
            value={todayKpi.active_users_today || 0} label="Usuarios activos" />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="dashboard-grid">
        {/* Progress ring + project stats */}
        <div className="card">
          <div className="card-header">
            <h3><TrendingUp size={18} /> Progreso general</h3>
          </div>
          <div className="card-body">
            <div className="big-progress">
              <div className="progress-ring">
                <svg viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#1E293B" strokeWidth="8" />
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#F59E0B" strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 52}`}
                    strokeDashoffset={`${2 * Math.PI * 52 * (1 - (p.avg_progress || 0) / 100)}`}
                    strokeLinecap="round" transform="rotate(-90 60 60)" />
                </svg>
                <span className="progress-value">{Math.round(p.avg_progress || 0)}%</span>
              </div>
            </div>
            <div className="stats-row">
              <div className="stat"><span className="stat-val">{p.completed || 0}</span><span className="stat-label">Completados</span></div>
              <div className="stat"><span className="stat-val">{p.active || 0}</span><span className="stat-label">En progreso</span></div>
              <div className="stat"><span className="stat-val">{p.on_hold || 0}</span><span className="stat-label">En espera</span></div>
            </div>
          </div>
        </div>

        {/* Task Status Pie Chart */}
        <div className="card">
          <div className="card-header">
            <h3><Activity size={18} /> Distribución de tareas</h3>
          </div>
          <div className="card-body" style={{ height: 240 }}>
            {taskStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={taskStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    paddingAngle={3} dataKey="value" nameKey="name" strokeWidth={0}>
                    {taskStatusData.map((_, i) => (
                      <Cell key={i} fill={statusColor(taskStatusData[i]?.status)} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#94A3B8' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="empty-text">Sin datos de tareas</p>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="dashboard-grid" style={{ marginTop: 20 }}>
        {/* Photo Activity Chart */}
        <div className="card">
          <div className="card-header">
            <h3><Camera size={18} /> Actividad fotográfica (14 días)</h3>
          </div>
          <div className="card-body" style={{ height: 240 }}>
            {photoChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={photoChartData}>
                  <defs>
                    <linearGradient id="photoGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.amber} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.amber} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total" stroke={COLORS.amber} fill="url(#photoGrad)" name="Total" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="empty-text">Sin datos de fotos recientes</p>
            )}
          </div>
        </div>

        {/* Tasks by Category */}
        <div className="card">
          <div className="card-header">
            <h3><BarChart3 size={18} /> Tareas por categoría</h3>
          </div>
          <div className="card-body" style={{ height: 240 }}>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="completed" name="Completadas" fill={COLORS.green} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="total" name="Total" fill={COLORS.blue} radius={[4, 4, 0, 0]} opacity={0.4} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="empty-text">Sin datos de categorías</p>
            )}
          </div>
        </div>
      </div>

      {/* Projects + Activity Row */}
      <div className="dashboard-grid" style={{ marginTop: 20 }}>
        {/* Active projects quick view */}
        <div className="card">
          <div className="card-header">
            <h3><FolderKanban size={18} /> Proyectos activos</h3>
            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/projects')}>Ver todos</button>
          </div>
          <div className="card-body">
            {projects.length > 0 ? (
              <div className="quick-projects">
                {(Array.isArray(projects) ? projects : []).slice(0, 6).map(proj => (
                  <div key={proj.id} className="quick-project" onClick={() => navigate(`/projects/${proj.id}`)}>
                    <div className="qp-info">
                      <span className="qp-code">{proj.code}</span>
                      <span className="qp-name">{proj.name}</span>
                    </div>
                    <div className="qp-progress">
                      <div className="progress-bar" style={{ width: 80 }}>
                        <div className="progress-fill" style={{ width: `${proj.progress_percentage || 0}%` }} />
                      </div>
                      <span className="qp-pct">{Math.round(proj.progress_percentage || 0)}%</span>
                    </div>
                    <ArrowRight size={14} className="qp-arrow" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text">No hay proyectos activos</p>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3><Clock size={18} /> Actividad reciente</h3>
          </div>
          <div className="card-body">
            <div className="activity-list">
              {(data?.recentActivity || []).slice(0, 8).map((a, i) => (
                <div key={i} className="activity-item">
                  <div className={`activity-dot ${a.status}`} />
                  <div className="activity-content">
                    <span className="activity-text">{a.name}</span>
                    <span className="activity-meta">{a.project_code} · {a.project_name}</span>
                  </div>
                  <span className="activity-time">
                    <Clock size={12} /> {new Date(a.updated_at).toLocaleDateString('es')}
                  </span>
                </div>
              ))}
              {(!data?.recentActivity?.length) && (
                <p className="empty-text">No hay actividad reciente.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Sub-components
// ============================================
function KpiCard({ icon, color, value, label, badge, badgeColor, trend, onClick, small }) {
  return (
    <div className={`kpi-card ${small ? 'kpi-card-sm' : ''}`} onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
      <div className={`kpi-icon ${color}`}>{icon}</div>
      <div className="kpi-data">
        <span className={`kpi-value ${small ? 'kpi-value-sm' : ''}`}>{value}</span>
        <span className="kpi-label">{label}</span>
      </div>
      {badge && <span className={`kpi-badge ${badgeColor || color}`}>{badge}</span>}
      {trend !== undefined && trend !== 0 && (
        <span className={`kpi-trend ${trend > 0 ? 'up' : 'down'}`}>
          {trend > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
  );
}

function statusLabelShort(s) {
  const labels = { completed: 'Completadas', in_progress: 'En progreso', pending: 'Pendientes', blocked: 'Bloqueadas', overdue: 'Vencidas' };
  return labels[s] || s || '?';
}

function statusColor(s) {
  const colors = { completed: COLORS.green, in_progress: COLORS.blue, pending: COLORS.gray, blocked: COLORS.red, overdue: COLORS.red };
  return colors[s] || COLORS.gray;
}
